import { IonContent } from '@ionic/react';
import React from 'react';
import { useFormContext } from 'react-hook-form';

import { amplitudeModule } from '@/modules/amplitude';
import { webCampaign } from '@/modules/webCampaign';
import {
  OnboardingOptionItem,
  OnboardingOptionsList,
} from '@/pages/Onboarding/components/List';
import { NavigationRange } from '@/pages/Onboarding/components/NavigationRange';
import { useOnboarding } from '@/pages/Onboarding/context';
import { LONELINESS_OPTIONS } from '@/pages/Onboarding/options/Loneliness/LONELINESS_OPTIONS';
import { OnbOptionType } from '@/pages/Onboarding/types';
import { nextStepTimeout } from '@/pages/Onboarding/utils/nextStepTimeout';
import { userService } from '@/services/user';
import { canLogEvents } from '@/utils/canLogEvents';

import { OwnAiTitle } from '../../components/Title';
import { LayoutDataType } from '../../types';

import { LonelinessStyles } from './Loneliness.styles';

export const ScreenLoneliness: React.FC = () => {
  React.useEffect(() => {
    if (canLogEvents()) {
      amplitudeModule.logEvent('onboarding_screen', {
        seen: 'Loneliness',
      });
    }
  }, []);

  const shouldAnimate = webCampaign.isAnimated();
  const [showElements, setShowElements] = React.useState<boolean>(true);

  const { wizard } = useOnboarding();

  const form = useFormContext<LayoutDataType>();

  const loneliness = form.watch('loneliness');

  const handleChange = (
    option: OnbOptionType<LayoutDataType['loneliness']>,
  ) => {
    const hasUserLoneliness = option.id !== 'never';

    userService.createOrUpdate({
      onboarding_data: {
        loneliness: option.title,
      },
    });

    let nextStepFn: () => void;

    if (hasUserLoneliness) {
      nextStepFn = wizard.nextStep;
    } else {
      nextStepFn = wizard.goToStep.bind(wizard, 'setting_up_profile');
    }

    if (shouldAnimate) {
      setTimeout(() => {
        if (shouldAnimate) {
          setShowElements(false);
        }
        nextStepTimeout(nextStepFn);
      }, 200);
    } else {
      nextStepTimeout(nextStepFn);
    }
  };

  const handleBackStep = () => {
    if (shouldAnimate) {
      setShowElements(false);
      nextStepTimeout(() => wizard.goToStep('name'));
    } else {
      wizard.goToStep('name');
    }
  };

  return (
    <LonelinessStyles background="dark-violet">
      <NavigationRange onBack={handleBackStep} />

      <OwnAiTitle className="title" animate={shouldAnimate} show={showElements}>
        Reflecting on loneliness: do you ever feel it?
      </OwnAiTitle>

      <IonContent>
        <OnboardingOptionsList>
          {LONELINESS_OPTIONS.map((option) => (
            <OnboardingOptionItem
              checked={loneliness === option.id}
              control={form.control}
              option={option}
              name="loneliness"
              key={option.id}
              onChange={() => handleChange(option)}
              theme="dark-violet"
              animate={shouldAnimate}
              show={showElements}
            />
          ))}
        </OnboardingOptionsList>
      </IonContent>
    </LonelinessStyles>
  );
};
